import { AIModule } from '../types';

export const AI_BIAS_FAIRNESS_MODULES: AIModule[] = [
  {
    id: 'ai-bias-fairness',
    title: 'AI Bias and Fairness',
    description: 'Understand and mitigate bias in AI systems to ensure fair and equitable outcomes.',
    estimatedTime: '100 minutes',
    duration: '100 minutes',
    difficulty: 'intermediate' as const,
    content: `
# AI Bias and Fairness

## Understanding AI Bias

### Types of Bias in AI Systems

#### 1. Historical Bias
Bias present in training data that reflects past discrimination.

\`\`\`python
import pandas as pd
import numpy as np
from sklearn.model_selection import train_test_split
from sklearn.ensemble import RandomForestClassifier
from sklearn.metrics import confusion_matrix, classification_report

class BiasDetector:
    def __init__(self):
        self.protected_attributes = []
        self.fairness_metrics = {}
        
    def detect_historical_bias(self, data: pd.DataFrame, target_column: str, 
                             protected_column: str) -> dict:
        """Detect historical bias in dataset"""
        bias_analysis = {}
        
        # Analyze outcome distribution by protected attribute
        outcome_by_group = data.groupby(protected_column)[target_column].agg([
            'mean', 'count', 'std'
        ]).round(3)
        
        bias_analysis['outcome_distribution'] = outcome_by_group
        
        # Calculate base rate difference
        groups = data[protected_column].unique()
        if len(groups) == 2:
            group1, group2 = groups
            rate1 = data[data[protected_column] == group1][target_column].mean()
            rate2 = data[data[protected_column] == group2][target_column].mean()
            
            bias_analysis['base_rate_difference'] = abs(rate1 - rate2)
            bias_analysis['disparate_impact_ratio'] = min(rate1, rate2) / max(rate1, rate2)
            
        return bias_analysis
        
    def analyze_representation_bias(self, data: pd.DataFrame, 
                                  protected_column: str) -> dict:
        """Analyze representation bias in dataset"""
        group_counts = data[protected_column].value_counts()
        total_samples = len(data)
        
        representation_analysis = {
            'group_sizes': group_counts.to_dict(),
            'group_percentages': (group_counts / total_samples * 100).round(2).to_dict(),
            'underrepresented_groups': []
        }
        
        # Identify underrepresented groups (< 10% of data)
        for group, percentage in representation_analysis['group_percentages'].items():
            if percentage < 10:
                representation_analysis['underrepresented_groups'].append(group)
                
        return representation_analysis

# Example usage
def create_biased_dataset():
    """Create a synthetic dataset with known bias"""
    np.random.seed(42)
    n_samples = 1000
    
    # Create features
    data = {
        'age': np.random.normal(35, 10, n_samples),
        'education_years': np.random.normal(14, 3, n_samples),
        'experience': np.random.normal(8, 5, n_samples),
        'gender': np.random.choice(['M', 'F'], n_samples, p=[0.7, 0.3]),  # Representation bias
        'race': np.random.choice(['White', 'Black', 'Hispanic', 'Asian'], n_samples, 
                               p=[0.6, 0.15, 0.15, 0.1])  # Representation bias
    }
    
    df = pd.DataFrame(data)
    
    # Create biased target variable (hiring decision)
    # Historical bias: different standards for different groups
    def biased_hiring_decision(row):
        base_score = (row['education_years'] * 0.3 + 
                     row['experience'] * 0.4 + 
                     (row['age'] - 25) * 0.1)
        
        # Add bias: lower threshold for males and certain races
        if row['gender'] == 'M':
            base_score += 1.5  # Gender bias
        if row['race'] == 'White':
            base_score += 1.0  # Racial bias
            
        # Add noise
        base_score += np.random.normal(0, 1)
        
        return 1 if base_score > 5 else 0
        
    df['hired'] = df.apply(biased_hiring_decision, axis=1)
    return df

# Analyze bias
biased_data = create_biased_dataset()
detector = BiasDetector()

gender_bias = detector.detect_historical_bias(biased_data, 'hired', 'gender')
race_bias = detector.detect_historical_bias(biased_data, 'hired', 'race')

print("Gender bias analysis:", gender_bias)
print("Race bias analysis:", race_bias)
\`\`\`

#### 2. Algorithmic Bias
Bias introduced by the algorithm itself.

\`\`\`python
class AlgorithmicBiasAnalyzer:
    def __init__(self, model, protected_attributes):
        self.model = model
        self.protected_attributes = protected_attributes
        
    def calculate_fairness_metrics(self, X_test, y_test, y_pred, 
                                 protected_attr_values):
        """Calculate various fairness metrics"""
        metrics = {}
        
        for attr_name, attr_values in protected_attr_values.items():
            attr_metrics = {}
            
            # Demographic Parity (Independence)
            overall_positive_rate = np.mean(y_pred)
            group_positive_rates = {}
            
            for group in np.unique(attr_values):
                group_mask = attr_values == group
                group_pred = y_pred[group_mask]
                group_true = y_test[group_mask]
                
                # Positive prediction rate
                positive_rate = np.mean(group_pred)
                group_positive_rates[group] = positive_rate
                
                # True positive rate (TPR) - Equality of Opportunity
                if np.sum(group_true) > 0:
                    tpr = np.sum((group_pred == 1) & (group_true == 1)) / np.sum(group_true)
                    attr_metrics[f'{group}_tpr'] = tpr
                    
                # False positive rate (FPR)
                if np.sum(group_true == 0) > 0:
                    fpr = np.sum((group_pred == 1) & (group_true == 0)) / np.sum(group_true == 0)
                    attr_metrics[f'{group}_fpr'] = fpr
                    
                # Predictive parity (precision)
                if np.sum(group_pred) > 0:
                    precision = np.sum((group_pred == 1) & (group_true == 1)) / np.sum(group_pred)
                    attr_metrics[f'{group}_precision'] = precision
                    
            # Calculate disparities
            rates = list(group_positive_rates.values())
            attr_metrics['demographic_parity_difference'] = max(rates) - min(rates)
            attr_metrics['demographic_parity_ratio'] = min(rates) / max(rates) if max(rates) > 0 else 0
            
            # Equal opportunity difference (TPR difference)
            tpr_values = [v for k, v in attr_metrics.items() if k.endswith('_tpr')]
            if len(tpr_values) >= 2:
                attr_metrics['equal_opportunity_difference'] = max(tpr_values) - min(tpr_values)
                
            metrics[attr_name] = attr_metrics
            
        return metrics
        
    def test_individual_fairness(self, X1, X2, threshold=0.1):
        """Test if similar individuals receive similar predictions"""
        # Calculate similarity (simplified - use appropriate distance metric)
        similarity = 1 / (1 + np.linalg.norm(X1 - X2))
        
        # Get predictions
        pred1 = self.model.predict_proba(X1.reshape(1, -1))[0]
        pred2 = self.model.predict_proba(X2.reshape(1, -1))[0]
        
        # Calculate prediction difference
        pred_diff = np.linalg.norm(pred1 - pred2)
        
        # Individual fairness: similar inputs should have similar outputs
        fairness_violation = pred_diff > threshold and similarity > 0.8
        
        return {
            'similarity': similarity,
            'prediction_difference': pred_diff,
            'fairness_violation': fairness_violation
        }

# Example fairness analysis
def analyze_model_fairness():
    # Create and train a model on biased data
    biased_data = create_biased_dataset()
    
    # Prepare features and target
    feature_cols = ['age', 'education_years', 'experience']
    X = biased_data[feature_cols]
    y = biased_data['hired']
    
    X_train, X_test, y_train, y_test = train_test_split(X, y, test_size=0.2, random_state=42)
    
    # Train model
    model = RandomForestClassifier(random_state=42)
    model.fit(X_train, y_train)
    
    # Make predictions
    y_pred = model.predict(X_test)
    
    # Analyze fairness
    analyzer = AlgorithmicBiasAnalyzer(model, ['gender', 'race'])
    
    protected_attrs = {
        'gender': biased_data.loc[X_test.index, 'gender'].values,
        'race': biased_data.loc[X_test.index, 'race'].values
    }
    
    fairness_metrics = analyzer.calculate_fairness_metrics(
        X_test, y_test, y_pred, protected_attrs
    )
    
    return fairness_metrics

fairness_results = analyze_model_fairness()
for attr, metrics in fairness_results.items():
    print(f"\n{attr} fairness metrics:")
    for metric, value in metrics.items():
        print(f"  {metric}: {value:.3f}")
\`\`\`

## Bias Mitigation Techniques

### 1. Pre-processing Methods

\`\`\`python
from sklearn.preprocessing import StandardScaler
from sklearn.decomposition import PCA

class PreprocessingBiasMitigation:
    def __init__(self):
        self.scaler = StandardScaler()
        
    def data_augmentation(self, X, y, protected_attr, minority_group):
        """Augment data to balance representation"""
        minority_mask = protected_attr == minority_group
        minority_X = X[minority_mask]
        minority_y = y[minority_mask]
        
        # Generate synthetic samples using SMOTE-like approach
        augmented_X = []
        augmented_y = []
        
        for i in range(len(minority_X)):
            # Find k nearest neighbors
            distances = np.linalg.norm(minority_X - minority_X[i], axis=1)
            nearest_indices = np.argsort(distances)[1:6]  # Exclude self
            
            # Generate synthetic samples
            for j in nearest_indices[:2]:  # Generate 2 samples per original
                # Linear interpolation
                alpha = np.random.random()
                synthetic_sample = alpha * minority_X[i] + (1 - alpha) * minority_X[j]
                
                augmented_X.append(synthetic_sample)
                augmented_y.append(minority_y[i])
                
        return np.vstack([X, np.array(augmented_X)]), np.hstack([y, np.array(augmented_y)])
        
    def remove_protected_correlation(self, X, protected_attr):
        """Remove correlation with protected attributes"""
        # Center the data
        X_centered = self.scaler.fit_transform(X)
        
        # Project away from protected attribute
        protected_centered = protected_attr - np.mean(protected_attr)
        
        # Calculate projection
        dot_product = np.dot(X_centered.T, protected_centered)
        norm_squared = np.dot(protected_centered, protected_centered)
        
        if norm_squared > 0:
            projection = np.outer(dot_product / norm_squared, protected_centered)
            X_debiased = X_centered - projection.T
        else:
            X_debiased = X_centered
            
        return X_debiased
        
    def reweighting(self, X, y, protected_attr):
        """Reweight samples to balance group representation"""
        weights = np.ones(len(X))
        
        # Calculate weights for each group
        unique_groups = np.unique(protected_attr)
        for group in unique_groups:
            group_mask = protected_attr == group
            group_positive_rate = np.mean(y[group_mask])
            overall_positive_rate = np.mean(y)
            
            if group_positive_rate > 0:
                # Reweight to match overall positive rate
                positive_weight = overall_positive_rate / group_positive_rate
                negative_weight = (1 - overall_positive_rate) / (1 - group_positive_rate)
                
                weights[group_mask & (y == 1)] *= positive_weight
                weights[group_mask & (y == 0)] *= negative_weight
                
        return weights

# Example usage
def mitigate_preprocessing_bias():
    biased_data = create_biased_dataset()
    
    # Encode categorical variables
    gender_encoded = (biased_data['gender'] == 'M').astype(int)
    
    feature_cols = ['age', 'education_years', 'experience']
    X = biased_data[feature_cols].values
    y = biased_data['hired'].values
    
    mitigator = PreprocessingBiasMitigation()
    
    # Method 1: Data augmentation
    X_aug, y_aug = mitigator.data_augmentation(
        X, y, biased_data['gender'].values, 'F'
    )
    print(f"Original data size: {len(X)}, Augmented: {len(X_aug)}")
    
    # Method 2: Remove correlation with protected attribute
    X_debiased = mitigator.remove_protected_correlation(X, gender_encoded)
    
    # Method 3: Reweighting
    sample_weights = mitigator.reweighting(X, y, gender_encoded)
    
    return X_aug, y_aug, X_debiased, sample_weights

mitigate_preprocessing_bias()
\`\`\`

### 2. In-processing Methods

\`\`\`python
class FairClassifier:
    """Classifier with fairness constraints during training"""
    
    def __init__(self, base_model, fairness_constraint='demographic_parity', 
                 constraint_weight=1.0):
        self.base_model = base_model
        self.fairness_constraint = fairness_constraint
        self.constraint_weight = constraint_weight
        
    def fit(self, X, y, protected_attr):
        """Train with fairness constraints"""
        # This is a simplified implementation
        # Real implementations would modify the loss function
        
        if self.fairness_constraint == 'demographic_parity':
            return self._fit_demographic_parity(X, y, protected_attr)
        elif self.fairness_constraint == 'equal_opportunity':
            return self._fit_equal_opportunity(X, y, protected_attr)
        else:
            # Standard training
            return self.base_model.fit(X, y)
            
    def _fit_demographic_parity(self, X, y, protected_attr):
        """Train with demographic parity constraint"""
        # Calculate reweighting for demographic parity
        weights = np.ones(len(X))
        
        unique_groups = np.unique(protected_attr)
        overall_positive_rate = np.mean(y)
        
        for group in unique_groups:
            group_mask = protected_attr == group
            group_size = np.sum(group_mask)
            
            # Weight to achieve demographic parity
            target_positives = overall_positive_rate * group_size
            actual_positives = np.sum(y[group_mask])
            
            if actual_positives > 0:
                adjustment = target_positives / actual_positives
                weights[group_mask & (y == 1)] *= adjustment
                
        return self.base_model.fit(X, y, sample_weight=weights)
        
    def _fit_equal_opportunity(self, X, y, protected_attr):
        """Train with equal opportunity constraint"""
        # Focus on true positive rates being equal across groups
        weights = np.ones(len(X))
        
        unique_groups = np.unique(protected_attr)
        
        # Calculate overall TPR for positive samples
        overall_tpr = np.mean(y)  # Simplified
        
        for group in unique_groups:
            group_mask = protected_attr == group
            positive_mask = y == 1
            group_positive_mask = group_mask & positive_mask
            
            if np.sum(group_positive_mask) > 0:
                group_tpr = np.mean(y[group_mask & positive_mask])
                if group_tpr > 0:
                    adjustment = overall_tpr / group_tpr
                    weights[group_positive_mask] *= adjustment
                    
        return self.base_model.fit(X, y, sample_weight=weights)
        
    def predict(self, X):
        return self.base_model.predict(X)
        
    def predict_proba(self, X):
        return self.base_model.predict_proba(X)

class AdversarialFairness:
    """Adversarial training for fairness"""
    
    def __init__(self, predictor_lr=0.001, adversary_lr=0.001):
        self.predictor_lr = predictor_lr
        self.adversary_lr = adversary_lr
        
    def build_networks(self, input_dim, num_classes, num_groups):
        """Build predictor and adversarial networks"""
        # This would use actual neural network framework like PyTorch/TensorFlow
        # Simplified representation here
        
        self.predictor = {
            'weights': np.random.randn(input_dim, num_classes) * 0.1,
            'bias': np.zeros(num_classes)
        }
        
        self.adversary = {
            'weights': np.random.randn(num_classes, num_groups) * 0.1,
            'bias': np.zeros(num_groups)
        }
        
    def train_adversarial(self, X, y, protected_attr, epochs=100):
        """Train with adversarial fairness"""
        num_samples = len(X)
        
        for epoch in range(epochs):
            # Forward pass through predictor
            pred_logits = np.dot(X, self.predictor['weights']) + self.predictor['bias']
            pred_probs = self._softmax(pred_logits)
            
            # Forward pass through adversary
            adv_logits = np.dot(pred_probs, self.adversary['weights']) + self.adversary['bias']
            adv_probs = self._softmax(adv_logits)
            
            # Calculate losses
            pred_loss = self._cross_entropy(pred_probs, y)
            adv_loss = self._cross_entropy(adv_probs, protected_attr)
            
            # Update predictor (minimize prediction loss, maximize adversary loss)
            pred_grad = self._calculate_predictor_gradient(X, y, pred_probs, adv_loss)
            self.predictor['weights'] -= self.predictor_lr * pred_grad
            
            # Update adversary (minimize adversary loss)
            adv_grad = self._calculate_adversary_gradient(pred_probs, protected_attr, adv_probs)
            self.adversary['weights'] -= self.adversary_lr * adv_grad
            
            if epoch % 20 == 0:
                print(f"Epoch {epoch}: Pred Loss: {pred_loss:.3f}, Adv Loss: {adv_loss:.3f}")
                
    def _softmax(self, x):
        exp_x = np.exp(x - np.max(x, axis=1, keepdims=True))
        return exp_x / np.sum(exp_x, axis=1, keepdims=True)
        
    def _cross_entropy(self, probs, targets):
        # Convert targets to one-hot if necessary
        if len(targets.shape) == 1:
            targets_onehot = np.eye(probs.shape[1])[targets]
        else:
            targets_onehot = targets
            
        return -np.mean(np.sum(targets_onehot * np.log(probs + 1e-10), axis=1))
        
    def _calculate_predictor_gradient(self, X, y, pred_probs, adv_loss):
        # Simplified gradient calculation
        return np.random.randn(*self.predictor['weights'].shape) * 0.01
        
    def _calculate_adversary_gradient(self, pred_probs, protected_attr, adv_probs):
        # Simplified gradient calculation
        return np.random.randn(*self.adversary['weights'].shape) * 0.01

# Example usage
def train_fair_model():
    biased_data = create_biased_dataset()
    
    # Prepare data
    feature_cols = ['age', 'education_years', 'experience']
    X = biased_data[feature_cols].values
    y = biased_data['hired'].values
    protected_attr = (biased_data['gender'] == 'M').astype(int)
    
    # Train fair classifier
    from sklearn.ensemble import RandomForestClassifier
    base_model = RandomForestClassifier(random_state=42)
    
    fair_clf = FairClassifier(
        base_model, 
        fairness_constraint='demographic_parity',
        constraint_weight=0.5
    )
    
    fair_clf.fit(X, y, protected_attr)
    
    # Compare with regular model
    regular_clf = RandomForestClassifier(random_state=42)
    regular_clf.fit(X, y)
    
    # Evaluate fairness
    regular_pred = regular_clf.predict(X)
    fair_pred = fair_clf.predict(X)
    
    print("Regular model predictions by gender:")
    print(pd.crosstab(biased_data['gender'], regular_pred))
    
    print("\nFair model predictions by gender:")
    print(pd.crosstab(biased_data['gender'], fair_pred))

train_fair_model()
\`\`\`

### 3. Post-processing Methods

\`\`\`python
class PostProcessingFairness:
    """Post-processing methods to ensure fairness"""
    
    def __init__(self, fairness_metric='demographic_parity'):
        self.fairness_metric = fairness_metric
        self.thresholds = {}
        
    def calibrate_thresholds(self, y_pred_proba, y_true, protected_attr):
        """Calibrate classification thresholds for fairness"""
        unique_groups = np.unique(protected_attr)
        
        if self.fairness_metric == 'demographic_parity':
            # Set thresholds to achieve equal positive prediction rates
            overall_positive_rate = np.mean(y_true)
            
            for group in unique_groups:
                group_mask = protected_attr == group
                group_probs = y_pred_proba[group_mask]
                
                # Find threshold that gives desired positive rate
                sorted_probs = np.sort(group_probs)[::-1]  # Descending order
                n_positive = int(overall_positive_rate * len(group_probs))
                
                if n_positive > 0 and n_positive < len(sorted_probs):
                    threshold = sorted_probs[n_positive]
                else:
                    threshold = 0.5
                    
                self.thresholds[group] = threshold
                
        elif self.fairness_metric == 'equal_opportunity':
            # Set thresholds to achieve equal true positive rates
            overall_tpr = np.mean(y_true)
            
            for group in unique_groups:
                group_mask = protected_attr == group
                group_probs = y_pred_proba[group_mask]
                group_true = y_true[group_mask]
                
                # Focus on positive samples
                positive_mask = group_true == 1
                if np.sum(positive_mask) > 0:
                    positive_probs = group_probs[positive_mask]
                    
                    # Find threshold for desired TPR
                    sorted_probs = np.sort(positive_probs)[::-1]
                    n_tp = int(overall_tpr * len(positive_probs))
                    
                    if n_tp > 0 and n_tp < len(sorted_probs):
                        threshold = sorted_probs[n_tp]
                    else:
                        threshold = 0.5
                else:
                    threshold = 0.5
                    
                self.thresholds[group] = threshold
                
        return self.thresholds
        
    def apply_fair_classification(self, y_pred_proba, protected_attr):
        """Apply group-specific thresholds for fair classification"""
        fair_predictions = np.zeros(len(y_pred_proba))
        
        unique_groups = np.unique(protected_attr)
        for group in unique_groups:
            group_mask = protected_attr == group
            threshold = self.thresholds.get(group, 0.5)
            
            fair_predictions[group_mask] = (y_pred_proba[group_mask] >= threshold).astype(int)
            
        return fair_predictions
        
    def equalized_odds_postprocessing(self, y_pred, y_true, protected_attr):
        """Post-process to achieve equalized odds"""
        # Calculate optimal randomization parameters for each group
        unique_groups = np.unique(protected_attr)
        flip_params = {}
        
        # Target rates (overall TPR and FPR)
        overall_tpr = np.sum((y_pred == 1) & (y_true == 1)) / np.sum(y_true == 1)
        overall_fpr = np.sum((y_pred == 1) & (y_true == 0)) / np.sum(y_true == 0)
        
        for group in unique_groups:
            group_mask = protected_attr == group
            group_pred = y_pred[group_mask]
            group_true = y_true[group_mask]
            
            # Calculate group-specific rates
            if np.sum(group_true == 1) > 0:
                group_tpr = np.sum((group_pred == 1) & (group_true == 1)) / np.sum(group_true == 1)
            else:
                group_tpr = 0
                
            if np.sum(group_true == 0) > 0:
                group_fpr = np.sum((group_pred == 1) & (group_true == 0)) / np.sum(group_true == 0)
            else:
                group_fpr = 0
                
            # Calculate flip probabilities
            # Simplified - in practice, solve optimization problem
            tpr_diff = overall_tpr - group_tpr
            fpr_diff = overall_fpr - group_fpr
            
            flip_params[group] = {
                'flip_positive_to_negative': max(0, -tpr_diff),
                'flip_negative_to_positive': max(0, tpr_diff)
            }
            
        # Apply randomized flipping
        fair_pred = y_pred.copy()
        
        for group in unique_groups:
            group_mask = protected_attr == group
            flip_p2n = flip_params[group]['flip_positive_to_negative']
            flip_n2p = flip_params[group]['flip_negative_to_positive']
            
            # Flip predictions randomly according to calculated probabilities
            positive_mask = group_mask & (y_pred == 1)
            negative_mask = group_mask & (y_pred == 0)
            
            # Flip some positives to negatives
            n_flip_p2n = int(flip_p2n * np.sum(positive_mask))
            if n_flip_p2n > 0:
                flip_indices = np.random.choice(
                    np.where(positive_mask)[0], 
                    size=n_flip_p2n, 
                    replace=False
                )
                fair_pred[flip_indices] = 0
                
            # Flip some negatives to positives
            n_flip_n2p = int(flip_n2p * np.sum(negative_mask))
            if n_flip_n2p > 0:
                flip_indices = np.random.choice(
                    np.where(negative_mask)[0], 
                    size=n_flip_n2p, 
                    replace=False
                )
                fair_pred[flip_indices] = 1
                
        return fair_pred

# Example usage
def demonstrate_postprocessing():
    biased_data = create_biased_dataset()
    
    # Train a model
    feature_cols = ['age', 'education_years', 'experience']
    X = biased_data[feature_cols].values
    y = biased_data['hired'].values
    protected_attr = (biased_data['gender'] == 'M').astype(int)
    
    from sklearn.ensemble import RandomForestClassifier
    model = RandomForestClassifier(random_state=42)
    model.fit(X, y)
    
    # Get predictions
    y_pred_proba = model.predict_proba(X)[:, 1]
    y_pred = model.predict(X)
    
    # Apply post-processing
    postprocessor = PostProcessingFairness('demographic_parity')
    thresholds = postprocessor.calibrate_thresholds(y_pred_proba, y, protected_attr)
    fair_pred = postprocessor.apply_fair_classification(y_pred_proba, protected_attr)
    
    print("Original predictions by gender:")
    print(pd.crosstab(biased_data['gender'], y_pred))
    
    print("\nFair predictions by gender:")
    print(pd.crosstab(biased_data['gender'], fair_pred))
    
    print(f"\nCalibrated thresholds: {thresholds}")

demonstrate_postprocessing()
\`\`\`

## Fairness Evaluation Framework

\`\`\`python
class FairnessEvaluator:
    """Comprehensive fairness evaluation framework"""
    
    def __init__(self):
        self.metrics = {}
        
    def evaluate_all_metrics(self, y_true, y_pred, y_pred_proba, protected_attr):
        """Evaluate all fairness metrics"""
        results = {}
        
        # Group fairness metrics
        results['demographic_parity'] = self._demographic_parity(y_pred, protected_attr)
        results['equal_opportunity'] = self._equal_opportunity(y_true, y_pred, protected_attr)
        results['equalized_odds'] = self._equalized_odds(y_true, y_pred, protected_attr)
        results['calibration'] = self._calibration(y_true, y_pred_proba, protected_attr)
        
        # Individual fairness (sample a few pairs)
        results['individual_fairness'] = self._sample_individual_fairness(
            y_pred_proba, protected_attr
        )
        
        # Intersectional fairness (if multiple protected attributes)
        if hasattr(protected_attr, 'columns'):
            results['intersectional'] = self._intersectional_fairness(
                y_true, y_pred, protected_attr
            )
            
        return results
        
    def _demographic_parity(self, y_pred, protected_attr):
        """Statistical parity / demographic parity"""
        groups = np.unique(protected_attr)
        rates = {}
        
        for group in groups:
            group_mask = protected_attr == group
            rates[f'group_{group}'] = np.mean(y_pred[group_mask])
            
        rate_values = list(rates.values())
        return {
            'rates': rates,
            'difference': max(rate_values) - min(rate_values),
            'ratio': min(rate_values) / max(rate_values) if max(rate_values) > 0 else 0
        }
        
    def _equal_opportunity(self, y_true, y_pred, protected_attr):
        """Equal opportunity (TPR equality)"""
        groups = np.unique(protected_attr)
        tprs = {}
        
        for group in groups:
            group_mask = protected_attr == group
            group_true = y_true[group_mask]
            group_pred = y_pred[group_mask]
            
            if np.sum(group_true) > 0:
                tpr = np.sum((group_pred == 1) & (group_true == 1)) / np.sum(group_true)
            else:
                tpr = 0
                
            tprs[f'group_{group}'] = tpr
            
        tpr_values = list(tprs.values())
        return {
            'tprs': tprs,
            'difference': max(tpr_values) - min(tpr_values)
        }
        
    def _equalized_odds(self, y_true, y_pred, protected_attr):
        """Equalized odds (TPR and FPR equality)"""
        groups = np.unique(protected_attr)
        rates = {}
        
        for group in groups:
            group_mask = protected_attr == group
            group_true = y_true[group_mask]
            group_pred = y_pred[group_mask]
            
            # TPR
            if np.sum(group_true == 1) > 0:
                tpr = np.sum((group_pred == 1) & (group_true == 1)) / np.sum(group_true == 1)
            else:
                tpr = 0
                
            # FPR
            if np.sum(group_true == 0) > 0:
                fpr = np.sum((group_pred == 1) & (group_true == 0)) / np.sum(group_true == 0)
            else:
                fpr = 0
                
            rates[f'group_{group}'] = {'tpr': tpr, 'fpr': fpr}
            
        return rates
        
    def _calibration(self, y_true, y_pred_proba, protected_attr):
        """Predictive parity / calibration"""
        groups = np.unique(protected_attr)
        calibration = {}
        
        for group in groups:
            group_mask = protected_attr == group
            group_true = y_true[group_mask]
            group_proba = y_pred_proba[group_mask]
            
            # Bin predictions and calculate calibration
            bins = np.linspace(0, 1, 11)
            bin_centers = (bins[1:] + bins[:-1]) / 2
            
            calibration_error = 0
            for i in range(len(bins) - 1):
                bin_mask = (group_proba >= bins[i]) & (group_proba < bins[i + 1])
                if np.sum(bin_mask) > 0:
                    bin_accuracy = np.mean(group_true[bin_mask])
                    bin_confidence = np.mean(group_proba[bin_mask])
                    calibration_error += np.abs(bin_accuracy - bin_confidence) * np.sum(bin_mask)
                    
            calibration_error /= len(group_true)
            calibration[f'group_{group}'] = calibration_error
            
        return calibration
        
    def _sample_individual_fairness(self, y_pred_proba, protected_attr, n_samples=100):
        """Sample individual fairness violations"""
        violations = 0
        total_comparisons = 0
        
        # Sample pairs of individuals
        n = len(y_pred_proba)
        for _ in range(n_samples):
            i, j = np.random.choice(n, 2, replace=False)
            
            # Skip if same protected group (looking for cross-group fairness)
            if protected_attr[i] == protected_attr[j]:
                continue
                
            # Calculate prediction difference
            pred_diff = abs(y_pred_proba[i] - y_pred_proba[j])
            
            # If predictions are very different for similar individuals, it's a violation
            # (This is simplified - would need proper similarity metric)
            if pred_diff > 0.3:  # Threshold for violation
                violations += 1
                
            total_comparisons += 1
            
        if total_comparisons > 0:
            violation_rate = violations / total_comparisons
        else:
            violation_rate = 0
            
        return {'violation_rate': violation_rate, 'comparisons': total_comparisons}
        
    def generate_fairness_report(self, evaluation_results):
        """Generate comprehensive fairness report"""
        report = {
            'overall_fairness_score': 0.0,
            'critical_issues': [],
            'recommendations': []
        }
        
        # Analyze demographic parity
        demo_parity = evaluation_results['demographic_parity']
        if demo_parity['difference'] > 0.1:  # 10% threshold
            report['critical_issues'].append(
                f"High demographic parity violation: {demo_parity['difference']:.3f}"
            )
            report['recommendations'].append(
                "Consider post-processing threshold adjustment or resampling"
            )
            
        # Analyze equal opportunity
        equal_opp = evaluation_results['equal_opportunity']
        if equal_opp['difference'] > 0.1:
            report['critical_issues'].append(
                f"Equal opportunity violation: {equal_opp['difference']:.3f}"
            )
            report['recommendations'].append(
                "Implement equal opportunity constraints during training"
            )
            
        # Calculate overall score (simplified)
        fairness_score = 1.0
        fairness_score -= min(0.5, demo_parity['difference'] * 2)
        fairness_score -= min(0.3, equal_opp['difference'] * 2)
        
        report['overall_fairness_score'] = max(0.0, fairness_score)
        
        if not report['critical_issues']:
            report['critical_issues'].append("No critical fairness issues detected")
            
        return report

# Comprehensive evaluation example
def comprehensive_fairness_evaluation():
    biased_data = create_biased_dataset()
    
    # Train model
    feature_cols = ['age', 'education_years', 'experience']
    X = biased_data[feature_cols].values
    y = biased_data['hired'].values
    protected_attr = (biased_data['gender'] == 'M').astype(int)
    
    from sklearn.ensemble import RandomForestClassifier
    model = RandomForestClassifier(random_state=42)
    model.fit(X, y)
    
    y_pred = model.predict(X)
    y_pred_proba = model.predict_proba(X)[:, 1]
    
    # Evaluate fairness
    evaluator = FairnessEvaluator()
    results = evaluator.evaluate_all_metrics(y, y_pred, y_pred_proba, protected_attr)
    
    # Generate report
    report = evaluator.generate_fairness_report(results)
    
    print("Fairness Evaluation Results:")
    print(f"Overall Fairness Score: {report['overall_fairness_score']:.3f}")
    print("\nCritical Issues:")
    for issue in report['critical_issues']:
        print(f"- {issue}")
    print("\nRecommendations:")
    for rec in report['recommendations']:
        print(f"- {rec}")
        
    return results, report

comprehensive_fairness_evaluation()
\`\`\`

## Best Practices for Fair AI

### 1. Diverse and Representative Data
- Ensure balanced representation across all groups
- Regularly audit data for bias patterns
- Include domain experts from affected communities

### 2. Transparent Model Development
- Document all modeling decisions and assumptions
- Use interpretable models when possible
- Implement bias detection throughout development

### 3. Continuous Monitoring
- Monitor fairness metrics in production
- Set up alerts for fairness violations
- Regular retraining with fairness constraints

### 4. Stakeholder Involvement
- Include affected communities in the design process
- Conduct impact assessments
- Establish feedback mechanisms

### 5. Legal and Ethical Compliance
- Stay informed about relevant regulations
- Implement privacy-preserving techniques
- Consider societal impact beyond technical metrics
`,
    learningObjectives: [
      'Identify different types of bias in AI systems',
      'Implement bias mitigation techniques across the ML pipeline',
      'Evaluate fairness using comprehensive metrics',
      'Design fair AI systems with stakeholder involvement'
    ],
    quiz: {
      questions: [
        {
          id: 'bias-fairness-1',
          question: 'What is the difference between demographic parity and equal opportunity?',
          options: [
            'They are the same concept',
            'Demographic parity focuses on overall outcomes, equal opportunity on outcomes for positive cases',
            'Equal opportunity is easier to achieve',
            'Demographic parity only applies to binary classifications'
          ],
          correctAnswer: 1,
          explanation: 'Demographic parity requires equal positive prediction rates across groups, while equal opportunity requires equal true positive rates (focusing only on positive cases).'
        },
        {
          id: 'bias-fairness-2',
          question: 'When should you apply bias mitigation techniques?',
          options: [
            'Only after model deployment',
            'Only during model training',
            'Throughout the entire ML pipeline (pre-, in-, and post-processing)',
            'Only when bias is detected'
          ],
          correctAnswer: 2,
          explanation: 'Bias mitigation should be applied throughout the entire ML pipeline, with different techniques available for pre-processing, in-processing, and post-processing stages.'
        },
        {
          id: 'bias-fairness-3',
          question: 'What is individual fairness?',
          options: [
            'Fairness for each person individually',
            'Similar individuals should receive similar predictions',
            'Each group should have equal representation',
            'Personal data should be protected'
          ],
          correctAnswer: 1,
          explanation: 'Individual fairness requires that similar individuals (those who are alike with respect to relevant features) receive similar predictions or treatment.'
        },
        {
          id: 'bias-fairness-4',
          question: 'Why might achieving perfect demographic parity be problematic?',
          options: [
            'It\'s too expensive to implement',
            'It may conflict with accuracy and other fairness metrics',
            'It only works for binary classifications',
            'It requires too much data'
          ],
          correctAnswer: 1,
          explanation: 'Perfect demographic parity may conflict with accuracy and other fairness metrics, and in some cases may not be appropriate if there are legitimate differences between groups.'
        },
        {
          id: 'bias-fairness-5',
          question: 'What is adversarial training in the context of fairness?',
          options: [
            'Training against malicious attacks',
            'Using an adversarial network to remove bias-related information',
            'Training with adversarial examples',
            'Competing models against each other'
          ],
          correctAnswer: 1,
          explanation: 'In fairness contexts, adversarial training uses an adversarial network that tries to predict protected attributes from model predictions, forcing the main model to learn representations that don\'t reveal protected information.'
        }
      ]
    }
  }
];